/**
 * useFlashOnChange — returns `true` for ~800ms whenever the supplied
 * signature changes (skipping the very first render). Pair with the
 * `.flash-update` CSS class + `data-flash` attribute defined in
 * globals.css to drive a yellow→transparent highlight per
 * INTERACTION_SPEC §2 ("Triage row · single ticket update — flash
 * highlight, yellow → transparent over 800ms").
 *
 * Pass a stable string built from the fields you care about, e.g.
 * `${ticket.status}|${ticket.priority}|${ticket.assigneeUserId ?? ''}`.
 * Time-derived values (now, deadlines) should NOT be in the signature
 * — they'd flash on every clock tick.
 */
import { useEffect, useRef, useState } from 'react';

const FLASH_MS = 800;

export function useFlashOnChange(signature: string): boolean {
  const prev = useRef<string | null>(null);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    // First render — record but don't flash; we only highlight
    // *changes*, not initial appearance.
    if (prev.current === null) {
      prev.current = signature;
      return;
    }
    if (prev.current === signature) return;
    prev.current = signature;
    setFlashing(true);
    const handle = window.setTimeout(() => setFlashing(false), FLASH_MS);
    return () => window.clearTimeout(handle);
  }, [signature]);

  return flashing;
}
